// src/services/AuthService.js
import axios from 'axios';

const VERIFY_CACHE_MS = 2500;
const VERIFIED_SNAPSHOT_MAX_AGE_MS = 5 * 60 * 1000;

let verificationInFlight = null;
let verificationInFlightToken = '';
let cachedSession = null;
let cachedSessionToken = '';
let cachedSessionExpiresAt = 0;
let lastVerifiedSession = null;
let lastVerifiedToken = '';
let lastVerifiedAt = 0;

function toBool(value) {
  if (value === true || value === 1) return true;
  if (value === false || value === 0 || value == null) return false;

  const text = String(value).trim().toLowerCase();
  return text === 'true' || text === '1' || text === 'yes' || text === 'y';
}

function clearStoredSession() {
  localStorage.removeItem('token');
  localStorage.removeItem('username');
  localStorage.removeItem('role');
  localStorage.removeItem('can_use_normal_mode');
}

function resetVerificationState() {
  verificationInFlight = null;
  verificationInFlightToken = '';
  cachedSession = null;
  cachedSessionToken = '';
  cachedSessionExpiresAt = 0;
  lastVerifiedSession = null;
  lastVerifiedToken = '';
  lastVerifiedAt = 0;
}

function emptySession(options = {}) {
  const preserveStoredValues = options.preserveStoredValues !== false;

  return {
    isAuthenticated: false,
    username: preserveStoredValues
      ? (localStorage.getItem('username') || null)
      : null,
    role: preserveStoredValues
      ? (localStorage.getItem('role') || null)
      : null,
    canUseNormalMode: preserveStoredValues
      ? localStorage.getItem('can_use_normal_mode') === 'yes'
      : false,
    canUseAllTools: false,
    canUseCaseBuddy: false,
    canUseHermesPage: false,
    isAgentAccount: false,
    agentAccessEnabled: false,
    isRestricted: false,
    restrictionReason: '',
    restrictionUntil: null,
    verificationUnavailable: !!options.verificationUnavailable,
  };
}

function rememberVerifiedSession(token, session, cacheMilliseconds = VERIFY_CACHE_MS) {
  const normalized = { ...session };

  lastVerifiedToken = token;
  lastVerifiedSession = normalized;
  lastVerifiedAt = Date.now();

  cachedSessionToken = token;
  cachedSession = normalized;
  cachedSessionExpiresAt = Date.now() + Math.max(0, cacheMilliseconds);

  return { ...normalized };
}

function sessionFromVerifyData(token, data) {
  const username = data.username || localStorage.getItem('username') || null;
  const role = data.role ?? localStorage.getItem('role') ?? null;
  const canUseNormalMode = toBool(
    data.can_use_normal_mode ?? localStorage.getItem('can_use_normal_mode')
  );

  if (username) {
    localStorage.setItem('username', username);
  }

  if (role) {
    localStorage.setItem('role', role);
  } else {
    localStorage.removeItem('role');
  }

  localStorage.setItem(
    'can_use_normal_mode',
    canUseNormalMode ? 'yes' : 'no'
  );

  return rememberVerifiedSession(token, {
    isAuthenticated: true,
    username,
    role,
    canUseNormalMode,
    canUseAllTools: toBool(data.can_use_all_tools),
    canUseCaseBuddy: toBool(data.can_use_case_buddy),
    canUseHermesPage: toBool(data.can_use_hermes_page),
    isAgentAccount: toBool(data.is_agent_account),
    agentAccessEnabled: toBool(data.agent_access_enabled),
    isRestricted: toBool(data.is_restricted),
    restrictionReason: data.restriction_reason || '',
    restrictionUntil: data.restriction_until || null,
    verificationUnavailable: false,
  });
}

function verificationHttpStatus(error) {
  return Number(error?.response?.status || 0);
}

function invalidatesStoredSession(error) {
  const status = verificationHttpStatus(error);

  // A 403 can be an IP/network policy denial rather than an invalid JWT.
  // Preserve the token in that case. Invalid or missing credentials are
  // returned as 400/401 by verify.php and the shared guard.
  return status === 400 || status === 401;
}

function verifySession(options = {}) {
  const force = options.force === true;
  const token = localStorage.getItem('token') || '';

  if (!token) {
    resetVerificationState();
    return Promise.resolve(emptySession({ preserveStoredValues: false }));
  }

  if (
    !force &&
    cachedSession &&
    cachedSessionToken === token &&
    Date.now() < cachedSessionExpiresAt
  ) {
    return Promise.resolve({ ...cachedSession });
  }

  if (
    verificationInFlight &&
    verificationInFlightToken === token
  ) {
    return verificationInFlight;
  }

  const requestPromise = axios.post('etherscan/verify.php', { token })
    .then(response => {
      if (response?.data?.status !== 'success') {
        clearStoredSession();
        resetVerificationState();
        return emptySession({ preserveStoredValues: false });
      }

      return sessionFromVerifyData(
        token,
        response.data.data || {}
      );
    })
    .catch(error => {
      if (invalidatesStoredSession(error)) {
        clearStoredSession();
        resetVerificationState();
        return emptySession({ preserveStoredValues: false });
      }

      // A network failure, 429, 500, or 503 should fail the current route
      // check closed without erasing a potentially valid user session.
      return emptySession({
        preserveStoredValues: true,
        verificationUnavailable: true,
      });
    });

  verificationInFlight = requestPromise;
  verificationInFlightToken = token;

  requestPromise.finally(() => {
    if (verificationInFlight === requestPromise) {
      verificationInFlight = null;
      verificationInFlightToken = '';
    }
  });

  return requestPromise;
}

const AuthService = {
  login(username, password) {
    return axios.post('etherscan/login.php', { username, password })
      .then(response => {
        if (response?.data?.status !== 'success') {
          return false;
        }

        const token = String(response.data.token || '').trim();
        if (!token) {
          return false;
        }

        resetVerificationState();

        localStorage.setItem('token', token);
        localStorage.setItem('username', username);

        const role = response.data.role || null;
        const canUseNormalMode = toBool(response.data.can_use_normal_mode);

        if (role) {
          localStorage.setItem('role', role);
        }

        localStorage.setItem(
          'can_use_normal_mode',
          canUseNormalMode ? 'yes' : 'no'
        );

        // Do not mark the full session as verified here. verify.php still needs
        // to load current restriction and permission state before the first
        // protected route is admitted. The router will perform one verification;
        // pageview logging will reuse that result without a second request.
        return true;
      })
      .catch(error => {
        console.error(
          'AuthService login: Error occurred during login:',
          error
        );
        return false;
      });
  },

  logout() {
    clearStoredSession();
    resetVerificationState();
  },

  getRole() {
    return localStorage.getItem('role') || null;
  },

  canUseNormalMode() {
    return localStorage.getItem('can_use_normal_mode') === 'yes';
  },

  isAuthenticated(options = {}) {
    return verifySession(options)
      .then(session => session.isAuthenticated === true);
  },

  getUserRole(options = {}) {
    return verifySession(options)
      .then(session => {
        if (!session.isAuthenticated) {
          return {
            role: null,
            canUseNormalMode: false,
            canUseCaseBuddy: false,
            isRestricted: false,
            restrictionReason: '',
            restrictionUntil: null,
            verificationUnavailable: !!session.verificationUnavailable,
          };
        }

        return {
          role: session.role,
          canUseNormalMode: session.canUseNormalMode,
          canUseCaseBuddy: session.canUseCaseBuddy,
          isRestricted: session.isRestricted,
          restrictionReason: session.restrictionReason,
          restrictionUntil: session.restrictionUntil,
          verificationUnavailable: false,
        };
      });
  },

  getSession(options = {}) {
    return verifySession(options);
  },

  refreshSession() {
    return verifySession({ force: true });
  },

  invalidateSessionCache() {
    cachedSession = null;
    cachedSessionToken = '';
    cachedSessionExpiresAt = 0;
  },

  getVerifiedSessionSnapshot(options = {}) {
    const token = localStorage.getItem('token') || '';
    const maximumAge = Number.isFinite(options.maximumAgeMilliseconds)
      ? Math.max(0, Number(options.maximumAgeMilliseconds))
      : VERIFIED_SNAPSHOT_MAX_AGE_MS;
    const snapshotAge = lastVerifiedAt > 0
      ? Date.now() - lastVerifiedAt
      : Number.POSITIVE_INFINITY;

    if (
      token &&
      token === lastVerifiedToken &&
      lastVerifiedSession?.isAuthenticated === true &&
      snapshotAge <= maximumAge
    ) {
      return { ...lastVerifiedSession };
    }

    return null;
  },

  getUsername() {
    return localStorage.getItem('username') || null;
  },
};

export default AuthService;
